import React from 'react'
import { AgentModel } from '../../../../models/AgentModel'
import { Option } from '../../../../models/Option'
import {
  IField, AddressField, EmailField, PhoneField, TextField, TextSelectField, validateFieldsEx, yesNoDataSource, BooleanRadioButtonField,
} from '../../../../shared/fields'
import { Consultant } from '../../../../models/Consultant'
import { ApplicationModel } from '../../../../models/ApplicationModel'
import { ApplicationFetch } from '../../../../controllers/ApplicationFetch'
import { Address } from '../../../../models/Address'

export interface AgentControlProps {
    model: ApplicationModel
    onChange: { (changes: Partial<ApplicationModel>, callback?: { (): any }): any }
    visible: boolean
    consultants: Consultant[]
    addressCountries: Option<string>[]
    preview?: boolean
}

export interface AgentControlState {

}

export class AgentControl extends React.Component<AgentControlProps, AgentControlState> implements IField {
  crmAgentGuid: TextSelectField
  agentGiveName: TextField
  agentFamilyName: TextField
  agentBusinessName: TextField
  agentAddress: AddressField
  agentPhone: PhoneField
  agentMobile: PhoneField
  agentEmail: EmailField
  agentWebsite: TextField
  container: HTMLDivElement
  physicalAddress: AddressField
  postalAddress: AddressField
  isPostalDifferent: BooleanRadioButtonField
  applicationFetch = new ApplicationFetch()

  constructor(props: AgentControlProps) {
    super(props)

    this.state = {

    }
  }

  componentDidMount() {
    const { model: { agentModel: { agentCode } } } = this.props
    this.getUpdatedAgentDetails(agentCode).then((retrieveAgent: Consultant) => {
      if (retrieveAgent) {
        this.updateAgentDetails(retrieveAgent)
      }
    })
  }

  componentDidUpdate(prevProps: AgentControlProps) {
    const { model, consultants } = this.props
    if (model.agentModel.crmAgentGuid !== prevProps.model.agentModel.crmAgentGuid) {
      if (!model.agentModel.crmAgentGuid) {
        const { onChange } = this.props
        const updatedAgent = {
          agentID: model.agentID,
          crmAgentGuid: null,
          givenName: '',
          familyName: '',
          businessName: '',
          email: '',
          phoneNumber: '',
          mobileNumber: '',
          physicalAddress: {} as unknown as Address,
          postalAddress: {} as unknown as Address,
          isPostalDifferent: null,
          webSite: '',
        }
        onChange(({ agentModel: updatedAgent }) as any)
      }
      if (model.agentModel.crmAgentGuid) {
        const agentCode = consultants.find((consultant) => consultant.id === model.agentModel.crmAgentGuid).code
        this.getUpdatedAgentDetails(agentCode).then(((retrieveAgent: Consultant) => {
          if (retrieveAgent) {
            this.updateAgentDetails(retrieveAgent)
          }
        }))
      }
    }
  }

  onChange = (change: Partial<AgentModel>, callback?: { (): any }) => {
    const { onChange, model } = this.props
    const agentModel = { ...model.agentModel, ...change }
    onChange({ agentModel })
  }

  getUpdatedAgentDetails = async (agentCode: string) => {
    const { model: { owningAccountID } } = this.props
    if (!agentCode) { return null }

    // Re-check Agent details are updated
    const updatedAgent = await this.applicationFetch.getLatestAgentsDetails(owningAccountID)
      .then((data: Consultant[]) => data.find((agent) => agent.code === agentCode))

    return updatedAgent
  }

  updateAgentDetails = (agentDetails: Consultant) => {
    const { onChange, model: { agentModel, agentID } } = this.props
    const updatedAgent = {
      agentID,
      givenName: agentDetails.firstName,
      agentCode: agentDetails.code,
      crmAgentGuid: agentModel.crmAgentGuid,
      familyName: agentDetails.lastName,
      businessName: agentDetails.businessName,
      phoneNumber: agentDetails.phone,
      mobileNumber: agentDetails.mobile,
      email: agentDetails.email,
      abn: agentDetails.abn,
      position: agentModel.position,
      webSite: agentDetails.agentWebsite,
      physicalAddress: agentDetails.address,
      isPostalDifferent: agentModel.isPostalDifferent,
      postalAddress: {},
      invalid: false,
    }
    onChange(({ agentModel: updatedAgent }) as any)
  }

  validate(callback: { (res: boolean): any }, focus?: boolean) {
    // Make another call to check Agent details
    const { model: { agentModel: { agentCode } } } = this.props
    this.getUpdatedAgentDetails(agentCode).then((retrieveAgent) => {
      if (retrieveAgent) {
        this.updateAgentDetails(retrieveAgent)
      }
    })

    validateFieldsEx(
      focus,
      callback,
      this.crmAgentGuid,
      this.agentGiveName,
      this.agentFamilyName,
      this.agentBusinessName,
      this.agentPhone,
      this.agentMobile,
      this.agentEmail,
      this.physicalAddress,
      this.postalAddress,
      this.isPostalDifferent,
    )
  }

  render() {
    const {
      visible, consultants, model, preview, addressCountries,
    } = this.props
    return (
      <>
        <p>84. If you engaged an agent who prepared your application or assisted you in preparing this application, please provide your agent’s contact details:</p>
        <TextSelectField
          ref={(r) => { this.crmAgentGuid = r }}
          id="agentCode"
          label="Agent"
          value={model.agentModel?.crmAgentGuid}
          onChange={(crmAgentGuid) => this.onChange({ crmAgentGuid })}
          required={visible}
          dataSource={consultants.map((c) => ({ value: c.id, label: c.code }))}
          block={false}
          preview={preview}
        />

        <div className="clearfix">
          <TextField
            ref={(r) => { this.agentGiveName = r }}
            className="float-left mr-3"
            id="giveName"
            label="First name"
            value={model.agentModel?.givenName}
            onChange={(givenName) => this.onChange({ givenName })}
            required
            maxLength={50}
            preview={preview}
          />
          <TextField
            ref={(r) => { this.agentFamilyName = r }}
            className="float-left"
            id="agentFamilyName"
            label="Last name"
            value={model.agentModel?.familyName}
            onChange={(familyName) => this.onChange({ familyName })}
            required={visible}
            maxLength={50}
            preview={preview}
          />
        </div>

        <TextField
          ref={(r) => { this.agentBusinessName = r }}
          id="agentBuisnessName"
          label="Business name"
          value={model.agentModel?.businessName}
          onChange={(businessName) => this.onChange({ businessName })}
          required={visible}
          maxLength={80}
          preview={preview}
        />

        <EmailField
          ref={(r) => { this.agentEmail = r }}
          id="agentEmail"
          label="Email address"
          value={model.agentModel?.email}
          onChange={(email) => this.onChange({ email })}
          required={visible}
          maxLength={80}
          preview={preview}
        />

        <PhoneField
          ref={(r) => { this.agentPhone = r }}
          id="agentPhone"
          label="Telephone number (e.g. +61 2 9999 9999)"
          value={model.agentModel?.phoneNumber}
          onChange={(phoneNumber) => this.onChange({ phoneNumber })}
          required={visible}
          block={false}
          preview={preview}
        />

        <PhoneField
          ref={(r) => { this.agentMobile = r }}
          id="agentMobile"
          label="Mobile phone number (e.g. +61 499 999 999)"
          value={model.agentModel?.mobileNumber}
          onChange={(mobileNumber) => this.onChange({ mobileNumber })}
          required={visible}
          block={false}
          preview={preview}
        />

        <AddressField
          ref={(r) => { this.physicalAddress = r }}
          id="agentPhysicalAddress"
          label="Street address"
          value={model.agentModel?.physicalAddress}
          onChange={(physicalAddress) => this.onChange({ physicalAddress })}
          required
          australianAddressesOnly
          countries={addressCountries}
          preview={preview}
        />

        <BooleanRadioButtonField
          ref={(r) => { this.isPostalDifferent = r }}
          id="agentPostalAddressDifferent"
          label="Is the postal address different to the one above?"
          value={model.agentModel?.isPostalDifferent}
          onChange={(isPostalDifferent) => this.onChange({ isPostalDifferent })}
          dataSource={yesNoDataSource}
          required
          preview={preview}
        />

        { model.agentModel.isPostalDifferent && (
        <AddressField
          ref={(r) => { this.postalAddress = r }}
          id="agentPostalAddress"
          label="Postal address"
          value={model.agentModel?.postalAddress}
          onChange={(postalAddress) => this.onChange({ postalAddress })}
          required={model.agentModel.isPostalDifferent}
          australianAddressesOnly
          countries={addressCountries}
          preview={preview}
        />
        )}

        <TextField
          ref={(r) => { this.agentWebsite = r }}
          id="agentWebsite"
          label="Website"
          value={model.agentModel?.webSite}
          onChange={(webSite) => this.onChange({ webSite })}
          maxLength={80}
          preview={preview}
        />
      </>
    )
  }
}
